import { useState, useEffect, useMemo } from "react";

import { useFetcher } from "@remix-run/react";
import { Check, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { PartSimilarityErrorBoundary } from "~/components/error-boundary";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Separator } from "~/components/ui/separator";
import { H3, P } from "~/components/ui/typography";
import { useSession } from "~/hooks/use-session";

import type { ConfidenceLevel } from "../../parts-similarity/utils/confidence";
import {
    CurrentPartDetails,
    ExpandableMatchCard,
    type MatchData,
} from "./part-review-shared";

// AIDEV-NOTE: Single part similarity review - left/right layout
// Left: current part details from this session
// Right: list of past interactions that potentially match (expandable)

export interface PartSimilarityMatch {
    partId: string;
    confidence: ConfidenceLevel | number;
    reason: string;
}

interface SinglePartSimilarityReviewProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    sessionId: string;
    partInteractionId: string;
    onComplete?: (
        acceptedMatches: Array<{ interactionId: string; partId: string }>,
    ) => void;
}

// AIDEV-NOTE: ExpandableMatchCard and CurrentPartDetails now imported from part-review-shared.tsx

export function SinglePartSimilarityReview({
    isOpen,
    onOpenChange,
    sessionId,
    partInteractionId,
    onComplete,
}: SinglePartSimilarityReviewProps) {
    const { session, parts } = useSession(sessionId);
    // AIDEV-NOTE: Changed to Set for multi-select - user can select multiple parts to merge
    const [selectedMatches, setSelectedMatches] = useState<Set<string>>(
        new Set(),
    );
    const linkFetcher = useFetcher<{ success?: boolean; error?: string }>();
    const mergeFetcher = useFetcher<{ success?: boolean; error?: string }>();

    // Track if we're in the middle of a multi-step operation
    const isSubmitting =
        linkFetcher.state === "submitting" ||
        mergeFetcher.state === "submitting";

    // Find the part interaction from session data
    const partInteraction = parts.present.find(
        (p) => p.id === partInteractionId,
    );

    // Get similarity matches from rawReport
    // AIDEV-NOTE: Raw similarity data extends MatchData with optional createdAt for sorting
    const similarityMatches: Array<
        MatchData & {
            partCreatedAt?: string;
            existingPart?: MatchData["existingPart"] & { createdAt?: string };
        }
    > = session?.rawReport?.partSimilarities?.[partInteractionId] || [];

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedMatches(new Set());
        }
    }, [isOpen]);

    // Find the oldest selected part (by createdAt) for linking
    const oldestSelectedPart = useMemo(() => {
        if (selectedMatches.size === 0) return null;

        const selectedMatchData = similarityMatches.filter((m) =>
            selectedMatches.has(m.partId),
        );

        if (selectedMatchData.length === 0) return null;

        // Sort by createdAt (oldest first) - fallback to partId if no createdAt
        return selectedMatchData.reduce((oldest, current) => {
            const oldestDate =
                oldest.existingPart?.createdAt || oldest.partCreatedAt || "";
            const currentDate =
                current.existingPart?.createdAt || current.partCreatedAt || "";
            return currentDate < oldestDate ? current : oldest;
        });
    }, [selectedMatches, similarityMatches]);

    // Handle link API response - this triggers first, then merge if needed
    useEffect(() => {
        if (linkFetcher.data?.success && oldestSelectedPart) {
            // If only one part selected, we're done
            if (selectedMatches.size === 1) {
                toast.success("Part linked successfully");
                onComplete?.([
                    {
                        interactionId: partInteractionId,
                        partId: oldestSelectedPart.partId,
                    },
                ]);
                onOpenChange(false);
            } else if (selectedMatches.size > 1) {
                // Multiple parts selected - now merge them
                // Get part info for the merge (need name from oldest part)
                const partName =
                    oldestSelectedPart.partName ||
                    oldestSelectedPart.existingPart?.name ||
                    "Merged Part";

                mergeFetcher.submit(
                    {
                        sourcePartIds: Array.from(selectedMatches),
                        name: partName,
                    },
                    {
                        method: "POST",
                        action: "/api/parts/link",
                        encType: "application/json",
                    },
                );
            }
        } else if (linkFetcher.data?.error) {
            toast.error(linkFetcher.data.error);
        }
    }, [linkFetcher.data]);

    // Handle merge API response
    useEffect(() => {
        if (mergeFetcher.data?.success && oldestSelectedPart) {
            toast.success(
                `Linked and merged ${selectedMatches.size} parts successfully`,
            );
            onComplete?.([
                {
                    interactionId: partInteractionId,
                    partId: oldestSelectedPart.partId,
                },
            ]);
            onOpenChange(false);
        } else if (mergeFetcher.data?.error) {
            toast.error(mergeFetcher.data.error);
        }
    }, [mergeFetcher.data]);

    // Handle dismiss API response (uses linkFetcher for simplicity)
    useEffect(() => {
        // Check if this was a dismiss action (no selected matches but success)
        if (
            linkFetcher.data?.success &&
            selectedMatches.size === 0 &&
            !oldestSelectedPart
        ) {
            toast.success("Marked as new part");
            onComplete?.([]);
            onOpenChange(false);
        }
    }, [linkFetcher.data, selectedMatches.size, oldestSelectedPart]);

    const handleSelect = (partId: string) => {
        setSelectedMatches((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(partId)) {
                newSet.delete(partId);
            } else {
                newSet.add(partId);
            }
            return newSet;
        });
    };

    const handleComplete = () => {
        if (selectedMatches.size > 0 && oldestSelectedPart) {
            // First, link the interaction to the oldest selected part
            linkFetcher.submit(
                {
                    partInteractionId,
                    partId: oldestSelectedPart.partId,
                },
                {
                    method: "POST",
                    action: "/api/parts/link-interaction",
                    encType: "application/json",
                },
            );
        } else {
            onComplete?.([]);
            onOpenChange(false);
        }
    };

    // AIDEV-NOTE: Dismiss similarity suggestions by marking as "confirmed_new"
    const handleDismissSuggestions = () => {
        linkFetcher.submit(
            { partInteractionId },
            {
                method: "POST",
                action: "/api/parts/dismiss-similarity",
                encType: "application/json",
            },
        );
    };

    if (!partInteraction) {
        return null;
    }

    // Get attributes for the current part
    const currentAttributes = partInteraction.attributes || [];

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            analyticsId="single-part-similarity-review"
        >
            <DialogContent className="flex max-h-[85vh] w-full sm:w-[95vw] 2xl:w-[80vw] sm:max-w-none flex-col overflow-hidden">
                <PartSimilarityErrorBoundary onReset={() => {}}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="size-5" />
                            Link to Existing Part
                        </DialogTitle>
                        <DialogDescription>
                            Is this part the same as one you've worked with
                            before?
                        </DialogDescription>
                    </DialogHeader>

                    {similarityMatches.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-12">
                            <AlertCircle className="size-12 text-muted-foreground" />
                            <P className="text-muted-foreground">
                                No potential matches found for this part
                            </P>
                        </div>
                    ) : (
                        <div className="flex flex-1 gap-4 min-h-0">
                            {/* Left side: Current part details */}
                            <div className="w-1/2 flex flex-col min-h-0">
                                <H3 className="text-sm font-medium text-muted-foreground mb-2">
                                    This Session
                                </H3>
                                <CurrentPartDetails
                                    name={partInteraction.name}
                                    description={partInteraction.description}
                                    recap={partInteraction.recap}
                                    quote={partInteraction.quote}
                                    attributes={currentAttributes}
                                />
                            </div>

                            <Separator orientation="vertical" />

                            {/* Right side: Past interactions */}
                            <div className="w-1/2 flex flex-col min-h-0 overflow-hidden">
                                <H3 className="text-sm font-medium text-muted-foreground mb-2 ml-9">
                                    Previous Sessions (
                                    {similarityMatches.length})
                                </H3>
                                <ScrollArea className="flex-1">
                                    <div className="space-y-2 pr-4">
                                        {similarityMatches.map(
                                            (match, index) => (
                                                <ExpandableMatchCard
                                                    key={`${match.partId}-${index}`}
                                                    match={match}
                                                    isSelected={selectedMatches.has(
                                                        match.partId,
                                                    )}
                                                    onSelect={() =>
                                                        handleSelect(
                                                            match.partId,
                                                        )
                                                    }
                                                />
                                            ),
                                        )}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    )}

                    <Separator />

                    <DialogFooter className="flex justify-end gap-2 pt-2">
                        <Button
                            onClick={handleDismissSuggestions}
                            variant="ghost"
                            size="sm"
                            disabled={isSubmitting}
                        >
                            This is a new part
                        </Button>
                        <Button
                            onClick={handleComplete}
                            size="sm"
                            disabled={
                                selectedMatches.size === 0 || isSubmitting
                            }
                            isLoading={isSubmitting}
                        >
                            <Check className="size-4 mr-2" />
                            {selectedMatches.size > 1
                                ? `Link ${selectedMatches.size} Parts`
                                : "Link to Selected"}
                        </Button>
                    </DialogFooter>
                </PartSimilarityErrorBoundary>
            </DialogContent>
        </Dialog>
    );
}
